import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { LeadActivityFeedItem } from 'src/app/private/modules/components/leads/lead-activity-feed/lead-activity-feed.component';
import { LeadActivitySubTab } from 'src/app/private/modules/components/leads/lead-profile/lead-profile.component';
import { LeadService } from 'src/app/private/services/lead.service';
import { MeetingBookingService } from 'src/app/private/services/meeting-booking.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { buildCommCardData } from 'src/app/utils/communication-log.util';
import { buildMeetingCardData } from 'src/app/utils/meeting-booking.util';

const TAB_META: Record<
  LeadActivitySubTab,
  { title: string; addLabel?: string; showAdd: boolean; showActions: boolean }
> = {
  activity: { title: 'Activity Timeline', showAdd: false, showActions: false },
  note: { title: 'Notes Timeline', addLabel: 'Add Note', showAdd: true, showActions: true },
  email: { title: 'Email Timeline', addLabel: 'Send Email', showAdd: true, showActions: false },
  whatsapp: { title: 'WhatsApp Timeline', addLabel: 'Send WhatsApp', showAdd: true, showActions: false },
  task: { title: 'Tasks Timeline', addLabel: 'Add Task', showAdd: true, showActions: true },
  meeting: { title: 'Meetings Timeline', addLabel: 'Add Meeting', showAdd: true, showActions: true },
  call: { title: 'Calls Timeline', addLabel: 'Log Call', showAdd: true, showActions: false },
  followups: { title: 'Follow Ups Timeline', showAdd: false, showActions: false },
};

@Component({
  selector: 'app-lead-profile-activity-panel',
  templateUrl: './lead-profile-activity-panel.component.html',
  styleUrls: ['./lead-profile-activity-panel.component.scss'],
})
export class LeadProfileActivityPanelComponent implements OnInit, OnChanges {
  @Input() tab: LeadActivitySubTab = 'activity';
  @Input() editFieldValues: any;
  @Input() refreshToken = 0;
  @Output() changed = new EventEmitter<void>();
  @ViewChild('addNoteTpl') addNoteTpl!: TemplateRef<unknown>;
  @ViewChild('editNoteTpl') editNoteTpl!: TemplateRef<unknown>;
  @ViewChild('addTaskTpl') addTaskTpl!: TemplateRef<unknown>;
  @ViewChild('editTaskTpl') editTaskTpl!: TemplateRef<unknown>;
  @ViewChild('deleteTpl') deleteTpl!: TemplateRef<unknown>;
  @ViewChild('emailComposerTpl') emailComposerTpl!: TemplateRef<unknown>;
  @ViewChild('whatsappComposerTpl') whatsappComposerTpl!: TemplateRef<unknown>;
  @ViewChild('callComposerTpl') callComposerTpl!: TemplateRef<unknown>;
  @ViewChild('addMeetingTpl') addMeetingTpl!: TemplateRef<unknown>;
  @ViewChild('editMeetingTpl') editMeetingTpl!: TemplateRef<unknown>;

  items: LeadActivityFeedItem[] = [];
  loading = false;
  btnLoading = false;
  editNoteData: any;
  editTaskData: any;
  editMeetingData: any;
  deleteTarget: any;
  deleteKind: 'note' | 'task' | 'meeting' = 'note';

  constructor(
    private api: LeadService,
    private meetingBookingService: MeetingBookingService,
    private toast: ToastrService,
    private localStorage: LocalStorageService,
    private modalService: NgbModal
  ) {}

  get meta() {
    return TAB_META[this.tab] ?? TAB_META.activity;
  }

  get emptyHint(): string {
    const labels: Record<LeadActivitySubTab, string> = {
      activity: 'No activity yet.',
      note: 'No notes yet. Click + Add Note to create one.',
      email: 'No emails yet. Click + Send Email to compose.',
      whatsapp: 'No WhatsApp messages yet. Click + Send WhatsApp to compose.',
      task: 'No tasks yet. Click + Add Task to create one.',
      meeting: 'No meetings yet. Click + Add Meeting to schedule.',
      call: 'No calls logged yet. Click + Log Call to record.',
      followups: 'No follow-ups yet. Use the panel on the right to add one.',
    };
    return labels[this.tab] ?? 'No records yet.';
  }

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tab'] || changes['refreshToken'] || changes['editFieldValues']) {
      if (this.tab !== 'activity') {
        this.load();
      }
    }
  }

  load(): void {
    if (!this.editFieldValues?.lead_id || this.tab === 'activity') {
      return;
    }
    this.loading = true;
    const clientId = this.localStorage.getClientId();
    const leadId = this.editFieldValues.lead_id;

    switch (this.tab) {
      case 'note':
        this.api.getAllNotes({ client_id: clientId, lead_id: leadId }).subscribe({
          next: (res) => this.finish(this.mapNotes(res)),
          error: () => this.finish([]),
        });
        break;
      case 'task':
        this.api.getAllTasks({ client_id: clientId, lead_id: leadId }).subscribe({
          next: (res) => this.finish(this.mapTasks(res)),
          error: () => this.finish([]),
        });
        break;
      case 'email':
        this.loadCommLogs('email');
        break;
      case 'whatsapp':
        this.loadCommLogs('whatsapp');
        break;
      case 'call':
        this.loadCommLogs('call');
        break;
      case 'meeting':
        this.meetingBookingService
          .getAllMeetingDetails({
            client_id: clientId,
            lead_id: leadId,
          })
          .subscribe({
            next: (res) => this.finish(this.mapMeetings(res)),
            error: () => this.finish([]),
          });
        break;
      case 'followups':
        this.api
          .getLeadsFollowup({ client_id: clientId, lead_id: leadId })
          .subscribe({
            next: (res) => this.finish(this.mapFollowups(res)),
            error: () => this.finish([]),
          });
        break;
      default:
        this.loading = false;
    }
  }

  private loadCommLogs(type: string): void {
    const centerId = this.localStorage.getCenterId();
    const where: Record<string, unknown> = {
      lead_id: Number(this.editFieldValues.lead_id),
      client_id: Number(this.localStorage.getClientId()),
      communication_type: type,
    };
    if (centerId != null && centerId !== '' && centerId !== 'null') {
      where['center_id'] = Number(centerId);
    }

    this.api
      .getCommunicationHisLogs({
        where,
        page: 0,
        limit: 100,
        order: { col: '"communication_history_log"."createdAt"', dir: 'DESC' },
      })
      .subscribe({
        next: (res) => {
          const rows = res?.rows ?? res ?? [];
          this.finish(this.mapCommLogs(rows));
        },
        error: () => this.finish([]),
      });
  }

  private finish(items: LeadActivityFeedItem[]): void {
    this.items = items;
    this.loading = false;
  }

  private actor(u: any): string {
    if (!u) {
      return 'User';
    }
    return (
      [u.prefix, u.fname, u.lname].filter(Boolean).join(' ').trim() || u.email || 'User'
    );
  }

  private mapNotes(rows: any[]): LeadActivityFeedItem[] {
    return (rows || []).map((n) => ({
      id: n.note_id,
      actorName: this.actor(n.assignedUser),
      createdAt: n.createdAt,
      body: n.note_description || '',
      raw: n,
    }));
  }

  private mapTasks(rows: any[]): LeadActivityFeedItem[] {
    return (rows || []).map((t) => ({
      id: t.task_id,
      actorName: this.actor(t.assignedUser),
      createdAt: t.createdAt,
      body: [t.title, t.description].filter(Boolean).join(' — '),
      raw: t,
    }));
  }

  private mapMeetings(rows: any[]): LeadActivityFeedItem[] {
    return (rows || []).map((m) => {
      const meetingData = buildMeetingCardData(m);
      return {
        id: m.id || m.appointment_id,
        actorName: 'Meeting',
        createdAt: m.createdAt,
        body: '',
        meetingData,
        raw: m,
      };
    });
  }

  private mapCommLogs(rows: any[]): LeadActivityFeedItem[] {
    return (rows || []).map((c) => {
      const commData = buildCommCardData(c);
      return {
        id: c.id,
        actorName: commData.typeLabel,
        createdAt: c.createdAt,
        body: '',
        commData,
        raw: c,
      };
    });
  }

  private mapFollowups(rows: any[]): LeadActivityFeedItem[] {
    return (rows || []).map((f) => ({
      id: f.id,
      actorName: this.actor(f.user),
      createdAt: f.createdAt,
      body: [f.remarks, f.next_action].filter(Boolean).join(' — '),
      raw: f,
    }));
  }

  onAdd(): void {
    switch (this.tab) {
      case 'note':
        this.openModal(this.addNoteTpl);
        break;
      case 'task':
        this.openModal(this.addTaskTpl);
        break;
      case 'meeting':
        this.openModal(this.addMeetingTpl, 'lg');
        break;
      case 'email':
        this.openModal(this.emailComposerTpl);
        break;
      case 'whatsapp':
        this.openModal(this.whatsappComposerTpl, 'xl');
        break;
      case 'call':
        this.openModal(this.callComposerTpl);
        break;
    }
  }

  onEdit(item: LeadActivityFeedItem): void {
    if (this.tab === 'note') {
      this.editNoteData = item.raw;
      this.openModal(this.editNoteTpl);
    } else if (this.tab === 'task') {
      this.editTaskData = item.raw;
      this.openModal(this.editTaskTpl);
    } else if (this.tab === 'meeting') {
      this.editMeetingData = item.raw;
      this.openModal(this.editMeetingTpl);
    }
  }

  onDelete(item: LeadActivityFeedItem): void {
    if (this.tab === 'note') {
      this.deleteKind = 'note';
      this.deleteTarget = item.raw;
      this.openModal(this.deleteTpl);
    } else if (this.tab === 'task') {
      this.deleteKind = 'task';
      this.deleteTarget = item.raw;
      this.openModal(this.deleteTpl);
    } else if (this.tab === 'meeting') {
      this.deleteKind = 'meeting';
      this.deleteTarget = item.raw;
      this.openModal(this.deleteTpl);
    }
  }

  getMeetingId(target: any): string {
    if (!target) {
      return '';
    }
    let details = target.google_meet_details;
    if (typeof details === 'string') {
      try {
        details = JSON.parse(details);
      } catch (e) {
        details = null;
      }
    }
    return details?.meeting_id || target.meeting_id || '';
  }

  confirmDelete(modal: any): void {
    this.btnLoading = true;
    const clientId = this.localStorage.getClientId();
    const leadId = this.editFieldValues.lead_id;

    if (this.deleteKind === 'note') {
      this.api
        .deleteNote({
          note_id: this.deleteTarget.note_id,
          client_id: clientId,
          lead_id: leadId,
        })
        .subscribe({
          next: () => {
            this.toast.success('Note deleted');
            this.afterMutation(modal);
          },
          error: (e) => this.failDelete(e),
        });
    } else if (this.deleteKind === 'task') {
      this.api
        .deleteTask({
          task_id: this.deleteTarget.task_id,
          client_id: clientId,
          lead_id: leadId,
        })
        .subscribe({
          next: () => {
            this.toast.success('Task deleted');
            this.afterMutation(modal);
          },
          error: (e) => this.failDelete(e),
        });
    } else if (this.deleteKind === 'meeting') {
      this.api
        .deleteAppointment({
          id: this.deleteTarget.id,
          meeting_id: this.getMeetingId(this.deleteTarget),
          client_id: clientId,
          user_id: this.localStorage.getUserId(),
        })
        .subscribe({
          next: () => {
            this.toast.success('Meeting deleted');
            this.afterMutation(modal);
          },
          error: (e) => this.failDelete(e),
        });
    }
  }

  private afterMutation(modal: any): void {
    this.btnLoading = false;
    modal.dismiss();
    this.load();
    this.changed.emit();
  }

  private failDelete(err: any): void {
    this.toast.error(err?.error?.message || 'Delete failed');
    this.btnLoading = false;
  }

  onChildReload(modal?: { dismiss: () => void }): void {
    modal?.dismiss();
    this.load();
    this.changed.emit();
  }

  onComposerClosed(modal: { dismiss: () => void }): void {
    modal.dismiss();
    this.load();
    this.changed.emit();
  }

  onCommunicationLogged(): void {
    this.load();
    this.changed.emit();
  }

  private openModal(tpl: TemplateRef<unknown>, size: 'lg' | 'xl' = 'lg'): void {
    if (!tpl) {
      return;
    }
    this.modalService.open(tpl, { size, scrollable: true, centered: true });
  }
}
